/**
 * Gift certificates at the studio counter.
 *
 * A certificate is a way of paying, not a discount: tax is on the full price
 * and the certificate comes off last, after tax, fees and any discount code.
 * The order's grand total stays the full price. The backend takes the money
 * off the certificate when the order is marked paid, and refuses to start a
 * payment the certificate can't cover (fxrybackend/controllers/giftCertificates.js).
 */

/** Stripe won't take a card payment under 50 cents. */
export const CARD_MIN_CENTS = 50;

/** What the certificate pays and what is left for the card, in cents.
 *  `dueCents` is the total after tax, fees and discount.
 *
 *  If the certificate would leave the card less than 50 cents, it pays 50
 *  cents less instead and that stays on the certificate: a card can't be
 *  charged 12 cents. */
export const giftSplit = (dueCents, balanceCents) => {
  const due = Math.max(0, Math.round(Number(dueCents) || 0));
  const balance = Math.max(0, Math.round(Number(balanceCents) || 0));
  let appliedCents = Math.min(balance, due);
  if (due - appliedCents > 0 && due - appliedCents < CARD_MIN_CENTS) {
    appliedCents = Math.max(0, due - CARD_MIN_CENTS);
  }
  const payCents = due - appliedCents;
  return {
    appliedCents,
    payCents,
    coversAll: appliedCents > 0 && payCents === 0,
  };
};

/** The two fields the backend reads off the order payload. Nothing is sent
 *  when the certificate pays nothing (e.g. a 100% discount already did). */
export const giftPayloadFields = (gift, appliedCents) =>
  gift?.code && appliedCents > 0
    ? { gift_certificate_code: gift.code, gift_certificate_cents: appliedCents }
    : {};

export const centsToDollars = (cents) =>
  `$${(Math.round(Number(cents) || 0) / 100).toFixed(2)}`;

// ── Selling a gift card on the same bill ──────────────────────────────────────
// A gift card bought with a repair at the counter. It is not a service and it
// is not taxed: its face amount is money we owe until it is spent, so it adds
// to what the reader charges and to nothing else. The server prices it from
// the certificate it already holds (controllers/giftCertificates.js
// reservedSale), so the amount below is only ever what staff see.

/** The least a gift card may be sold for, as the gift page has it. */
export const GIFT_SALE_MIN_CENTS = 2500;

/** What a gift card added to the bill is worth, in cents. */
export const giftSaleCentsOf = (sale) =>
  Math.max(0, Math.round(Number(sale?.face_cents) || 0));

/** What the reader or the card is charged: the order's share, plus the card. */
export const chargedWithGiftSale = (payCents, sale) =>
  Math.max(0, Math.round(Number(payCents) || 0)) + giftSaleCentsOf(sale);

/** The one field the payment and the order carry, so the server can price the
 *  card itself. Nothing at all when no card is being sold. */
export const giftSaleFields = (sale) =>
  sale?.id ? { gift_sale_certificate_id: sale.id } : {};

/** Why this gift card cannot be added yet, or null when it can. */
export const giftSaleRefusal = ({ amountCents, code } = {}) => {
  if (!amountCents) return "Enter the gift card amount.";
  if (amountCents < GIFT_SALE_MIN_CENTS) {
    return `A gift card starts at ${centsToDollars(GIFT_SALE_MIN_CENTS)}.`;
  }
  if (!String(code || "").trim()) {
    return "Type the code from the back of the card.";
  }
  return null;
};

/** Dollars typed by staff as whole cents, or 0. "25", "25.50", "$25" all work. */
export const giftSaleAmountCents = (typed) => {
  const clean = String(typed ?? "").replace(/[$,\s]/g, "");
  if (clean === "") return 0;
  const amount = Number(clean);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.round(amount * 100);
};

// ── Selling one at the counter, on its own ───────────────────────────────────
// A gift card sold with no repair behind it: the counter's own screen
// (components/GiftCard.jsx) instead of walking over to superadmin. The server
// prices everything (controllers/giftCertificates.js sellInStudio), so the
// figures here are only what staff see before they press pay.

/** Longest name Natalie will stitch (helper/giftCertificates.js). */
export const ENVELOPE_NAME_MAX = 24;

/** What the card and the envelope come to, before the envelope's sales tax.
 *  The card is not taxed; Stripe adds the envelope's tax when the payment
 *  starts, so the reader asks for a little more than this. */
export const counterSaleCents = ({ amountCents = 0, envelopeCents = 0 } = {}) =>
  Math.max(0, Math.round(Number(amountCents) || 0)) +
  Math.max(0, Math.round(Number(envelopeCents) || 0));

/** Why this counter sale cannot be paid for yet, or null when it can. */
export const counterSaleRefusal = ({
  amountCents,
  code,
  envelopeStyle = "none",
  envelopeName = "",
  envelopesLeft = null,
} = {}) => {
  if (!amountCents) return "Enter the gift card amount.";
  if (amountCents < GIFT_SALE_MIN_CENTS) {
    return `A gift card starts at ${centsToDollars(GIFT_SALE_MIN_CENTS)}.`;
  }
  if (!String(code || "").trim()) {
    return "Type the code from the back of a printed card.";
  }
  if (envelopeStyle !== "none") {
    // Only the ready-made ones are stock. A named envelope is cut and
    // chainstitched to order, so it is still sellable at zero (Calley, 27 Oct
    // 2026: "the named one has not been made yet").
    if (
      envelopeStyle === "ready" &&
      Number.isFinite(envelopesLeft) &&
      envelopesLeft < 1
    ) {
      return "The ready-made envelopes have all gone. A named one can still be sold.";
    }
    if (envelopeStyle === "named") {
      const name = String(envelopeName || "").trim();
      if (!name) return "Type the name to stitch on the envelope.";
      if (name.length > ENVELOPE_NAME_MAX) {
        return `The stitched name can be up to ${ENVELOPE_NAME_MAX} characters.`;
      }
    }
  }
  return null;
};

/** The body POST /gift/sell reads. Nothing in it prices the sale. */
export const counterSaleBody = ({
  amountCents,
  code,
  envelopeStyle = "none",
  envelopeName = "",
  buyerName = "",
  buyerEmail = "",
} = {}) => ({
  amount_cents: Math.round(Number(amountCents) || 0),
  card_code: String(code || "").trim(),
  pay: "reader",
  buyer: {
    name: String(buyerName || "").trim(),
    email: String(buyerEmail || "").trim(),
  },
  ...(envelopeStyle === "none"
    ? {}
    : {
        envelope: {
          style: envelopeStyle,
          name_on_envelope:
            envelopeStyle === "named" ? String(envelopeName || "").trim() : "",
        },
      }),
});

/**
 * The stages at which a gift card sale can still be called off.
 *
 * Up to the moment the card is read, Cancel stops the reader and the sale is
 * off with nothing charged. Once `processPayment` has the card, Stripe
 * decides whether the money moves, so from there the only honest way back is
 * a refund. The same line the garment checkout and the Products screen draw.
 */
export const CANCELLABLE_GIFT_STAGES = ["connecting", "starting", "collecting"];

/** Whether the Cancel button shows, and whether it is allowed to act. */
export const canCancelGiftSale = (stage) =>
  CANCELLABLE_GIFT_STAGES.includes(stage);
